import { useCallback } from "react";
import axios, { AxiosResponse, AxiosRequestConfig } from "axios";

let apiVersion = process.env.REACT_APP_API_VERSION || "1";
export const setApiVersion = (version: string) => {
  apiVersion = version;
};

type ApiRequestMethod = "get" | "put" | "post" | "patch";

export const useApi = <T>() => {
  const sendRequest = useCallback(
    async (
      method: ApiRequestMethod,
      uri: string,
      data?: Object,
      config?: AxiosRequestConfig
    ): Promise<T> => {
      let response: AxiosResponse<T> | undefined = undefined;
      const defaultHeaders = { "Content-Type": "application/json" };
      const headers = { ...defaultHeaders, ...(config?.headers || {}) };

      const url = new URL(uri, window.location.origin);
      url.searchParams.append("api-version", apiVersion);

      try {
        if (method === "get") {
          response = await axios.get<T>(url.toString(), {
            ...config,
            headers
          });
        } else if (method === "put") {
          response = await axios.put<T>(url.toString(), data, {
            ...config,
            headers
          });
        } else if (method === "post") {
          response = await axios.post<T>(url.toString(), data, {
            ...config,
            headers
          });
        } else if (method === "patch") {
          response = await axios.patch<T>(url.toString(), data, {
            ...config,
            headers
          });
        } else {
          throw new Error(`Unsupported method: ${method}`);
        }
        return response.data as T;
      } catch (error) {
        logger.error(error);
        throw error;
      }
    },
    []
  );

  const logger = {
    error: (error: unknown) => {
      if (process.env.REACT_APP_ENV !== "production") {
        console.error("API Request Error:", error);
      }
    }
  };

  const get = useCallback(
    async (uri: string, config?: AxiosRequestConfig): Promise<T> => {
      return sendRequest("get", uri, undefined, config);
    },
    [sendRequest]
  );

  const put = useCallback(
    async (uri: string, data: Object, config?: AxiosRequestConfig): Promise<T> => {
      return sendRequest("put", uri, data, config);
    },
    [sendRequest]
  );

  const post = useCallback(
    async (uri: string, data: Object, config?: AxiosRequestConfig): Promise<T> => {
      return sendRequest("post", uri, data, config);
    },
    [sendRequest]
  );

  const patch = useCallback(
    async (uri: string, data: Object, config?: AxiosRequestConfig): Promise<T> => {
      return sendRequest("patch", uri, data, config);
    },
    [sendRequest]
  );

  return { get, put, post, patch };
};
